import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Typography,
  Button,
  Avatar,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Chip,
  Stack,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import VerifiedIcon from '@mui/icons-material/Verified';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import WarningIcon from '@mui/icons-material/Warning';
import backgroundImage from '../assets/chemistry2.jpg';
import logo from '../assets/images.png';
import Preloader from '../theme/Preloader';
import restaurantConfig from '../config/Organisation_Details';
import { getRedirectPathByRole } from '../utility/roleRedirect';

// Configure logger
const logger = {
  info: (message, data) => console.log(`[LOGIN] ${message}`, data || ''),
  error: (message, error) => console.error(`[LOGIN] ${message}`, error || ''),
  debug: (message, data) => console.debug(`[LOGIN] ${message}`, data || ''),
  warn: (message, data) => console.warn(`[LOGIN] ${message}`, data || ''),
};

// ============================================
// AUTHENTICATION SERVICE
// ============================================

// Replace the API configuration section with:
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

logger.info('API Configuration', { API_BASE_URL });

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    logger.error('Request interceptor error', error);
    return Promise.reject(error);
  }
);

const clearAuthData = () => {
  ['access_token', 'refresh_token', 'user_data', 'user_email', 'user_role', 'user_id', 
   'user_name', 'first_name', 'staff_id', 'department', 'phone', 'gender', 'is_active',
   'role', 'email', 'userId', 'userName', 'staffId', 'userDepartment', 'phoneNumber'].forEach(key => {
    localStorage.removeItem(key);
  });
  logger.info('Auth data cleared');
};
const storeUserData = (user) => {
  if (user) {
    // Store all user data
    localStorage.setItem('user_data', JSON.stringify(user));
    localStorage.setItem('user_email', user.email);
    localStorage.setItem('user_role', user.user_type);
    localStorage.setItem('user_id', user.id.toString());
    localStorage.setItem('user_name', user.display_name || user.full_name || user.email);
    localStorage.setItem('user_type', user.user_type);
    localStorage.setItem('is_active', user.is_active ? 'true' : 'false');
    localStorage.setItem('is_verified', user.is_verified ? 'true' : 'false');
    
    // Store role-specific data
    if (user.role_specific_data) {
      if (user.user_type === 'supplier') {
        localStorage.setItem('company_name', user.role_specific_data.company_name || '');
        // Make sure business_verified is stored correctly
        const bizVerified = user.role_specific_data.business_verified === 1 || 
                           user.role_specific_data.business_verified === true || 
                           user.role_specific_data.business_verified === '1' || 
                           user.role_specific_data.business_verified === 'true';
        localStorage.setItem('business_verified', bizVerified ? 'true' : 'false');
        console.log('✅ Supplier business_verified:', bizVerified);
      } else if (user.user_type === 'buyer') {
        localStorage.setItem('buyer_name', user.role_specific_data.name || '');
        localStorage.setItem('organisation', user.role_specific_data.organisation || '');
      }
    } else {
      // If role_specific_data is not in the user object, check if we need to fetch it
      console.warn('⚠️ No role_specific_data found in user object');
    }
    
    // Store additional legacy keys for compatibility
    localStorage.setItem('role', user.user_type);
    localStorage.setItem('email', user.email);
    localStorage.setItem('userId', user.id.toString());
    localStorage.setItem('userName', user.display_name || user.full_name || user.email);
    
    // Log all stored values for debugging
    console.log('📦 Stored user data:', {
      email: user.email,
      role: user.user_type,
      isVerified: localStorage.getItem('is_verified'),
      isActive: localStorage.getItem('is_active'),
      businessVerified: localStorage.getItem('business_verified'),
      hasRoleSpecificData: !!user.role_specific_data
    });
    
    logger.debug('User data stored', { email: user.email, role: user.user_type });
  }
};

const loginUser = async (email, password) => {
  try {
    logger.info('Login attempt', { email });
    const response = await api.post('/login.php', { email, password });
    logger.debug('Login response received', { status: response.status, hasUser: !!response.data.user });
    
    if (response.data && response.data.success) {
      const { access_token, refresh_token, user } = response.data;
      
      // Store tokens
      if (access_token) {
        localStorage.setItem('access_token', access_token);
      }
      if (refresh_token) {
        localStorage.setItem('refresh_token', refresh_token);
      }
      
      // Store user data
      storeUserData(user);
      
      // Store additional legacy keys for compatibility
      localStorage.setItem('role', user.user_type);
      localStorage.setItem('email', user.email);
      localStorage.setItem('userId', user.id.toString());
      localStorage.setItem('userName', user.display_name || user.full_name || user.email);
      
      logger.info('Login successful', { email: user.email, role: user.user_type });
      return { success: true, data: response.data, user };
    }
    
    // Handle validation errors
    if (response.data && response.data.validation_errors) {
      return { 
        success: false, 
        error: 'Account validation failed',
        validation_errors: response.data.validation_errors,
        user_type: response.data.user_type
      };
    }
    
    return { success: false, error: 'Invalid response from server' };
  } catch (error) {
    logger.error('Login failed', error);
    let errorMessage = 'Login failed. Please try again.';
    let validationErrors = [];
    
    if (error.response) {
      const errorData = error.response.data;
      
      // Check for validation errors
      if (errorData.validation_errors) {
        validationErrors = errorData.validation_errors;
        errorMessage = errorData.message || 'Account validation failed';
      } else {
        errorMessage = errorData.message || errorData.error || errorMessage;
      }
      
      if (error.response.status === 401) {
        errorMessage = 'Invalid email or password';
      } else if (error.response.status === 403) {
        errorMessage = 'Access denied. Please check your account status.';
        if (errorData.validation_errors) {
          validationErrors = errorData.validation_errors;
        }
      } else if (error.response.status === 422) {
        errorMessage = 'Validation error. Please check your input.';
      } else if (error.response.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      }
      
      logger.error('Login error details', { 
        status: error.response.status, 
        data: error.response.data 
      });
    } else if (error.request) {
      errorMessage = 'Network error. Please check your connection.';
    }
    
    return { 
      success: false, 
      error: errorMessage,
      validation_errors: validationErrors
    };
  }
};

// ============================================
// ANIMATED BRAND TEXT COMPONENT
// ============================================

const AnimatedBrandText = ({ 
  text = 'Chemical Database',
  typingSpeed = 70,
  deleteSpeed = 35,
  pauseDuration = 2500,
  colors = ['#1B5E20', '#2E7D32', '#4CAF50', '#388E3C'],
  fontFamily = 'Playfair Display, serif',
  fontSize = '3.5rem',
  fontWeight = 700,
}) => {
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [charStates, setCharStates] = useState([]);
  const [isComplete, setIsComplete] = useState(false);
  const indexRef = useRef(0);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const fullText = text;
    let currentIndex = 0;
    indexRef.current = 0;
    
    setCharStates(text.split('').map(() => ({
      appeared: false,
      scale: 0,
    })));

    const typeEffect = () => {
      if (!isDeleting) {
        if (currentIndex <= fullText.length) {
          const newText = fullText.substring(0, currentIndex);
          setDisplayText(newText);
          
          setCharStates(prev => {
            const newStates = [...prev];
            if (currentIndex > 0 && currentIndex <= fullText.length) {
              newStates[currentIndex - 1] = {
                ...newStates[currentIndex - 1],
                appeared: true,
                scale: 0,
              };
              setTimeout(() => {
                setCharStates(states => {
                  const updated = [...states];
                  if (updated[currentIndex - 1]) {
                    updated[currentIndex - 1].scale = 1;
                  }
                  return updated;
                });
              }, 20);
            }
            return newStates;
          });
          
          currentIndex++;
          indexRef.current = currentIndex;
          timeoutRef.current = setTimeout(typeEffect, typingSpeed);
        } else {
          setIsComplete(true);
          timeoutRef.current = setTimeout(() => {
            setIsDeleting(true);
            setIsComplete(false);
            timeoutRef.current = setTimeout(typeEffect, deleteSpeed);
          }, pauseDuration);
        }
      } else {
        if (currentIndex >= 0) {
          setDisplayText(fullText.substring(0, currentIndex));
          setCharStates(prev => {
            const newStates = [...prev];
            if (currentIndex < newStates.length) {
              newStates[currentIndex] = {
                ...newStates[currentIndex],
                appeared: false,
              };
            }
            return newStates;
          });
          currentIndex--;
          indexRef.current = currentIndex;
          timeoutRef.current = setTimeout(typeEffect, deleteSpeed);
        } else {
          setIsDeleting(false);
          currentIndex = 0;
          indexRef.current = 0;
          setCharStates(text.split('').map(() => ({
            appeared: false,
            scale: 0,
          })));
          setDisplayText('');
          timeoutRef.current = setTimeout(typeEffect, typingSpeed);
        }
      }
    };

    timeoutRef.current = setTimeout(typeEffect, 100);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [text, typingSpeed, deleteSpeed, pauseDuration, isDeleting]);

  const renderGradientText = () => {
    if (!displayText) return null;

    return displayText.split('').map((char, index) => {
      const colorIndex = index % colors.length;
      const color = colors[colorIndex];
      const charState = charStates[index] || { appeared: false, scale: 0 };

      if (char === ' ') {
        return <span key={index} style={{ display: 'inline-block', width: '0.35em' }}>&nbsp;</span>;
      }

      return (
        <span
          key={index}
          style={{
            color: color,
            display: 'inline-block',
            transform: `scale(${charState.scale || 0})`,
            opacity: charState.appeared ? 1 : 0,
            transition: 'all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
            textShadow: `
              0 0 30px ${color}50,
              0 0 60px ${color}25,
              0 0 90px ${color}10
            `,
            animation: charState.appeared && isComplete ? 'float 3s ease-in-out infinite' : 'none',
            animationDelay: `${index * 0.03}s`,
            fontWeight: color === '#FFFFFF' ? 800 : 700,
            letterSpacing: '0.03em',
          }}
        >
          {char}
        </span>
      );
    });
  };

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <Typography
        variant="h2"
        sx={{
          fontFamily: fontFamily,
          fontWeight: fontWeight,
          fontSize: fontSize,
          lineHeight: 1.2,
          mb: 1.5,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          minHeight: { xs: '3rem', md: '4.5rem' },
          '& span': {
            letterSpacing: '0.03em',
          },
        }}
      >
        {renderGradientText()}
        <span
          style={{
            display: 'inline-block',
            width: '3px',
            height: '0.7em',
            backgroundColor: colors[0],
            marginLeft: '3px',
            animation: 'blink 0.8s step-end infinite',
            verticalAlign: 'text-bottom',
            boxShadow: `0 0 20px ${colors[0]}40, 0 0 40px ${colors[0]}20`,
            borderRadius: '2px',
          }}
        />
      </Typography>

      <style>
        {`
          @keyframes blink {
            0%, 100% { 
              opacity: 1;
              transform: scaleY(1);
            }
            50% { 
              opacity: 0;
              transform: scaleY(0.6);
            }
          }
          @keyframes float {
            0%, 100% { 
              transform: translateY(0px);
            }
            50% { 
              transform: translateY(-4px);
            }
          }
        `}
      </style>
    </Box>
  );
};

// ============================================
// VALIDATION ERROR DIALOG
// ============================================

const ValidationErrorDialog = ({ open, onClose, errors, userType }) => {
  const getErrorIcon = () => {
    if (userType === 'supplier') {
      return <VerifiedIcon sx={{ fontSize: 40, color: '#FF9800' }} />;
    }
    return <WarningIcon sx={{ fontSize: 40, color: '#FF9800' }} />;
  };

  const getErrorTitle = () => {
    if (userType === 'supplier') {
      return 'Business Verification Required';
    }
    return 'Account Verification Required';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          background: 'linear-gradient(135deg, #FFF8E1 0%, #FFECB3 100%)',
          border: '2px solid #FF9800',
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, pb: 1 }}>
        {getErrorIcon()}
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#E65100' }}>
          {getErrorTitle()}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <DialogContentText component="div">
          <Typography variant="body1" sx={{ color: '#BF360C', mb: 2, fontWeight: 500 }}>
            Your account requires RMRDC verification before you can access the Dashboard.
          </Typography>
          
          <Box sx={{ bgcolor: 'rgba(255, 152, 0, 0.15)', p: 2, borderRadius: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ color: '#E65100', fontWeight: 600, mb: 1 }}>
              Please be patient:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {errors.map((error, index) => (
                <li key={index}>
                  <Typography variant="body2" sx={{ color: '#BF360C' }}>
                    {error}
                  </Typography>
                </li>
              ))}
            </ul>
          </Box>

          <Typography variant="body2" sx={{ color: '#795548' }}>
            {userType === 'supplier' 
              ? 'Please contact the admin to complete your business verification.'
              : 'Contact support@rawmaterialchemicals.com.ng if it lingers'}
          </Typography>
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button 
          onClick={onClose} 
          variant="contained"
          sx={{
            bgcolor: '#FF9800',
            color: '#fff',
            '&:hover': { bgcolor: '#F57C00' },
            borderRadius: 3,
            px: 4,
          }}
        >
          I Understand
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================
// LOGIN PAGE COMPONENT
// ============================================

const LoginPage = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({ email: '', password: '' });
  const [serverError, setServerError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [validationUserType, setValidationUserType] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();
  const hasRedirected = useRef(false);

  // Get restaurant details
  const restaurantName = 'Chemical Database';
  const restaurantSlogan = 'Transforming local resources into industrial success';
  const restaurantPhone = restaurantConfig.phone || '+234 800 123 4567';
  const restaurantEmail = restaurantConfig.email || 'info@chemicaldatabase.com';
  const restaurantAddress = restaurantConfig.address || '123 Chemical Avenue, Lagos, Nigeria';
  const operatingHours = restaurantConfig.operatingHours || 'Mon-Fri: 8:00 AM - 6:00 PM';
  const supportEmail = restaurantConfig.supportEmail || 'support@chemicaldatabase.com';
  const supportPhone = restaurantConfig.supportPhone || '+234 800 765 4321';

  const primaryColor = '#2E7D32';
  const primaryLight = '#4CAF50';
  const secondaryColor = '#C62828';
  const secondaryDark = '#B71C1C';
  const whiteColor = '#FFFFFF';

  const greenGradient = `linear-gradient(135deg, ${primaryColor} 0%, ${primaryLight} 100%)`;
  const redGradient = `linear-gradient(135deg, ${secondaryColor} 0%, ${secondaryDark} 100%)`;

  // Check if already authenticated
  useEffect(() => {
    const checkAuth = async () => {
      if (hasRedirected.current) return;
      
      const token = localStorage.getItem('access_token');
      const userData = localStorage.getItem('user_data');
      
      if (token && userData) {
        logger.info('User already authenticated, redirecting');
        hasRedirected.current = true;
        const userRole = localStorage.getItem('user_role') || 'user';
        const redirectPath = getRedirectPathByRole(userRole);
        navigate(redirectPath, { replace: true });
        return;
      }
      setIsPageLoading(false);
    };
    
    checkAuth();
  }, [navigate]);

  const validate = () => {
    const newErrors = { email: '', password: '' };
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return !newErrors.email && !newErrors.password;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
    setServerError('');
    setSuccessMessage('');
    setValidationErrors([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    if (isLoading) return;

    setIsLoading(true);
    setServerError('');
    setSuccessMessage('');
    setValidationErrors([]);

    try {
      logger.info('Login attempt', { email: formData.email });
      
      const result = await loginUser(formData.email, formData.password);
      
      if (result.success) {
        setSuccessMessage('Login successful!');
        setServerError('');

        const user = result.user;
        const role = user.user_type || 'user';

        // Store additional data for compatibility
        localStorage.setItem('role', role);
        localStorage.setItem('email', user.email);
        localStorage.setItem('userId', user.id.toString());
        localStorage.setItem('userName', user.display_name || user.full_name || user.email);
        localStorage.setItem('userDepartment', user.role_specific_data?.organisation || user.role_specific_data?.company_name || 'Not Assigned');

        const redirectPath = getRedirectPathByRole(role);
        logger.info('Redirecting to', { role, path: redirectPath });
        
        setTimeout(() => {
          navigate(redirectPath, { replace: true });
        }, 300);
        
      } else {
        // Check for validation errors
        if (result.validation_errors && result.validation_errors.length > 0) {
          setValidationErrors(result.validation_errors);
          setValidationUserType(result.user_type || '');
          setShowValidationDialog(true);
          setServerError('');
        } else {
          setServerError(result.error || 'Login failed. Please try again.');
        }
        setIsLoading(false);
      }
    } catch (error) {
      logger.error('Login error', error);
      setServerError('An unexpected error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const features = [
    { icon: <SecurityIcon />, title: 'Secure', description: 'Enterprise-grade security' },
    { icon: <SpeedIcon />, title: 'Fast', description: 'Lightning quick access' },
    { icon: <VerifiedIcon />, title: 'Verified', description: 'Trusted by thousands' },
  ];

  if (isPageLoading) {
    return <Preloader text={`Loading ${restaurantName}...`} size={70} fullScreen={true} />;
  }

  if (isLoading) {
    return <Preloader text="Signing you in..." size={70} fullScreen={true} />;
  }

  return (
    <>
      <Box
        sx={{
          height: '100vh',
          width: '100vw',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.6) 100%), url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          padding: 0,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Grid
          container
          sx={{
            maxWidth: 1400,
            width: '100%',
            height: '100vh',
            position: 'relative',
            padding: { xs: 2, md: 4 },
          }}
        >
          {/* LEFT SIDE - Brand Section */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              justifyContent: 'center',
              padding: 6,
              position: 'relative',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                zIndex: 2,
                maxWidth: 550,
                width: '100%',
              }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  mb: 3,
                  p: 1.5,
                  background: 'rgba(255,255,255,0.95)',
                  backdropFilter: 'blur(15px)',
                  borderRadius: '20px',
                  border: '1px solid rgba(46, 125, 50, 0.2)',
                  boxShadow: `
                    0 8px 32px rgba(46, 125, 50, 0.15),
                    0 2px 8px rgba(0,0,0,0.08),
                    inset 0 1px 0 rgba(255,255,255,0.6)
                  `,
                }}
              >
                <Box
                  component="img"
                  src={logo}
                  alt={restaurantName}
                  sx={{
                    width: 100,
                    height: 'auto',
                    maxHeight: 100,
                    objectFit: 'contain',
                    filter: `
                      drop-shadow(0 4px 24px rgba(46, 125, 50, 0.3))
                      drop-shadow(0 0 60px rgba(46, 125, 50, 0.1))
                      brightness(1.05) 
                      contrast(1.05)
                    `,
                    animation: 'pulseGlow 3s ease-in-out infinite, subtleFloat 4s ease-in-out infinite',
                    position: 'relative',
                    zIndex: 1,
                    display: 'block',
                    borderRadius: '12px',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      filter: `
                        drop-shadow(0 6px 32px rgba(46, 125, 50, 0.4))
                        drop-shadow(0 0 80px rgba(46, 125, 50, 0.15))
                        brightness(1.1) 
                        contrast(1.1)
                      `,
                    },
                  }}
                />
              </Box>
              
              <AnimatedBrandText 
                text="Chemical Database"
                typingSpeed={70}
                deleteSpeed={35}
                pauseDuration={2500}
                colors={['#1B5E20', '#2E7D32', '#43A047', '#388E3C']}
                fontFamily="'Playfair Display', serif"
                fontSize={{ xs: '2.2rem', md: '3.8rem' }}
                fontWeight={800}
              />

              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(255,255,255,0.95)',
                  fontSize: '1.15rem',
                  lineHeight: 1.6,
                  mb: 4,
                  maxWidth: 450,
                  fontWeight: 400,
                  letterSpacing: '0.5px',
                  textShadow: '0 2px 20px rgba(0,0,0,0.4)',
                  width: '100%',
                }}
              >
                {restaurantSlogan}
              </Typography>

              <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
                {features.map((feature, index) => (
                  <Chip
                    key={index}
                    icon={feature.icon}
                    label={feature.title}
                    sx={{
                      backgroundColor: 'rgba(255,255,255,0.08)',
                      backdropFilter: 'blur(10px)',
                      color: whiteColor,
                      border: '1px solid rgba(255,255,255,0.1)',
                      '& .MuiChip-icon': {
                        color: primaryLight,
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.15)',
                        transform: 'translateY(-2px)',
                        transition: 'all 0.3s ease',
                      },
                    }}
                  />
                ))}
              </Stack>

              <Box
                sx={{
                  display: 'flex',
                  gap: 4,
                  borderTop: '1px solid rgba(255,255,255,0.08)',
                  paddingTop: 3,
                }}
              >
                {[
                  { value: '99.9%', label: 'Uptime' },
                  { value: '24/7', label: 'Support' },
                ].map((stat, index) => (
                  <Box key={index}>
                    <Typography
                      variant="h5"
                      sx={{
                        color: whiteColor,
                        fontWeight: 700,
                        fontSize: '1.5rem',
                        textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255,255,255,0.5)',
                        fontSize: '0.75rem',
                        letterSpacing: 1,
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  mt: 3,
                  pt: 2,
                  borderTop: '1px solid rgba(255,255,255,0.05)',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(255,255,255,0.4)',
                    fontSize: '0.7rem',
                    letterSpacing: 0.5,
                  }}
                >
                  {restaurantAddress}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(255,255,255,0.4)',
                    fontSize: '0.7rem',
                    letterSpacing: 0.5,
                  }}
                >
                  {restaurantPhone} | {restaurantEmail}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(255,255,255,0.3)',
                    fontSize: '0.65rem',
                    letterSpacing: 0.5,
                  }}
                >
                  {operatingHours}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                position: 'absolute',
                top: '20%',
                right: '-10%',
                width: 400,
                height: 400,
                borderRadius: '50%',
                background: `radial-gradient(circle, ${primaryColor}15 0%, transparent 70%)`,
                filter: 'blur(60px)',
                zIndex: 0,
                animation: 'orbit 20s linear infinite',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: '10%',
                left: '-5%',
                width: 300,
                height: 300,
                borderRadius: '50%',
                background: `radial-gradient(circle, ${primaryLight}15 0%, transparent 70%)`,
                filter: 'blur(60px)',
                zIndex: 0,
                animation: 'orbit 25s linear infinite reverse',
              }}
            />
          </Grid>

          <style>
            {`
              @keyframes pulseGlow {
                0%, 100% {
                  filter: drop-shadow(0 4px 24px rgba(46, 125, 50, 0.3)) drop-shadow(0 0 40px rgba(46, 125, 50, 0.1)) brightness(1.05) contrast(1.05);
                }
                50% {
                  filter: drop-shadow(0 4px 40px rgba(46, 125, 50, 0.5)) drop-shadow(0 0 80px rgba(46, 125, 50, 0.2)) brightness(1.1) contrast(1.1);
                }
              }
              @keyframes subtleFloat {
                0%, 100% { transform: translateY(0px); }
                50% { transform: translateY(-4px); }
              }
              @keyframes orbit {
                0% { transform: rotate(0deg) translateX(50px) rotate(0deg); }
                100% { transform: rotate(360deg) translateX(50px) rotate(-360deg); }
              }
            `}
          </style>

          {/* RIGHT SIDE - Login Form */}
          <Grid
            item
            xs={12}
            md={5.5}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginLeft: { md: 'auto' },
              height: '100%',
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: 480,
                height: '100%',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Card
                sx={{
                  width: '100%',
                  background: 'rgba(255, 255, 255, 0.06)',
                  backdropFilter: 'blur(40px)',
                  borderRadius: '40px',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  boxShadow: '0 25px 80px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)',
                  padding: { xs: 3, sm: 4, md: 5 },
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 35px 100px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.08)',
                  },
                }}
              >
                <CardContent sx={{ p: 0, position: 'relative', zIndex: 1 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box
                      sx={{
                        display: { xs: 'flex', md: 'none' },
                        flexDirection: 'column',
                        alignItems: 'center',
                        mb: 3,
                      }}
                    >
                      <Box
                        component="img"
                        src={logo}
                        alt={restaurantName}
                        sx={{
                          width: 80,
                          height: 'auto',
                          mb: 1,
                          padding: '4px',
                          background: 'rgba(255,255,255,0.9)',
                          backdropFilter: 'blur(10px)',
                          borderRadius: '12px',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        }}
                      />
                    </Box>

                    <Avatar
                      sx={{
                        width: 72,
                        height: 72,
                        background: `linear-gradient(135deg, ${primaryColor}40, ${primaryLight}40)`,
                        backdropFilter: 'blur(10px)',
                        border: '2px solid rgba(255,255,255,0.15)',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                        mb: 2,
                      }}
                    >
                      <LockOutlinedIcon sx={{ fontSize: 32, color: whiteColor }} />
                    </Avatar>

                    <Typography
                      variant="h4"
                      gutterBottom
                      sx={{
                        fontWeight: 700,
                        color: whiteColor,
                        fontFamily: 'Playfair Display, serif',
                        fontSize: '2rem',
                        letterSpacing: 0.5,
                        textShadow: '0 2px 20px rgba(0,0,0,0.3)',
                      }}
                    >
                      Sign In
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255,255,255,0.5)',
                        mb: 3,
                        textAlign: 'center',
                        fontSize: '0.95rem',
                      }}
                    >
                      Enter your credentials to access your Dashboard
                    </Typography>

                    {serverError && (
                      <Alert 
                        severity="error" 
                        sx={{ 
                          mb: 2, 
                          width: '100%',
                          borderRadius: 3,
                        }}
                      >
                        <AlertTitle>Login Failed</AlertTitle>
                        {serverError}
                      </Alert>
                    )}

                    {successMessage && (
                      <Alert 
                        severity="success" 
                        sx={{ 
                          mb: 2, 
                          width: '100%',
                          borderRadius: 3,
                        }}
                      >
                        <AlertTitle>Success</AlertTitle>
                        {successMessage}
                      </Alert>
                    )}

                    <Box
                      component="form"
                      noValidate
                      onSubmit={handleSubmit}
                      sx={{ mt: 1, width: '100%' }}
                    >
                      <TextField
                        placeholder="Enter your email address"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        variant="outlined"
                        margin="normal"
                        fullWidth
                        type="email"
                        autoComplete="email"
                        error={!!errors.email}
                        helperText={errors.email}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailOutlinedIcon sx={{ color: 'rgba(255,255,255,0.3)' }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 4,
                            backgroundColor: 'rgba(255,255,255,0.04)',
                            backdropFilter: 'blur(10px)',
                            color: whiteColor,
                            transition: 'all 0.3s ease',
                            padding: '4px 8px',
                            '& fieldset': {
                              borderColor: 'rgba(255,255,255,0.06)',
                            },
                            '&:hover': {
                              backgroundColor: 'rgba(255,255,255,0.08)',
                              '& fieldset': {
                                borderColor: 'rgba(255,255,255,0.12)',
                              },
                            },
                            '&.Mui-focused': {
                              backgroundColor: 'rgba(255,255,255,0.1)',
                              '& fieldset': {
                                borderColor: primaryColor,
                                borderWidth: 2,
                              },
                            },
                            '& input': {
                              padding: '12px 8px',
                              color: whiteColor,
                              '&::placeholder': {
                                color: 'rgba(255,255,255,0.3)',
                                opacity: 1,
                              },
                            },
                          },
                          '& .MuiInputLabel-root': {
                            display: 'none',
                          },
                          '& .MuiFormHelperText-root': {
                            color: 'rgba(255,255,255,0.5)',
                            marginLeft: '8px',
                          },
                        }}
                      />

                      <TextField
                        placeholder="Enter your password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        variant="outlined"
                        margin="normal"
                        fullWidth
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        error={!!errors.password}
                        helperText={errors.password}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockOutlinedIcon sx={{ color: 'rgba(255,255,255,0.3)' }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={handleClickShowPassword}
                                edge="end"
                                sx={{ color: 'rgba(255,255,255,0.3)' }}
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 4,
                            backgroundColor: 'rgba(255,255,255,0.04)',
                            backdropFilter: 'blur(10px)',
                            color: whiteColor,
                            transition: 'all 0.3s ease',
                            padding: '4px 8px',
                            '& fieldset': {
                              borderColor: 'rgba(255,255,255,0.06)',
                            },
                            '&:hover': {
                              backgroundColor: 'rgba(255,255,255,0.08)',
                              '& fieldset': {
                                borderColor: 'rgba(255,255,255,0.12)',
                              },
                            },
                            '&.Mui-focused': {
                              backgroundColor: 'rgba(255,255,255,0.1)',
                              '& fieldset': {
                                borderColor: primaryColor,
                                borderWidth: 2,
                              },
                            },
                            '& input': {
                              padding: '12px 8px',
                              color: whiteColor,
                              '&::placeholder': {
                                color: 'rgba(255,255,255,0.3)',
                                opacity: 1,
                              },
                            },
                          },
                          '& .MuiInputLabel-root': {
                            display: 'none',
                          },
                          '& .MuiFormHelperText-root': {
                            color: 'rgba(255,255,255,0.5)',
                            marginLeft: '8px',
                          },
                        }}
                      />

                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        size="large"
                        disabled={isLoading}
                        sx={{
                          mt: 3,
                          py: 1.8,
                          textTransform: 'none',
                          fontWeight: 700,
                          fontSize: '1rem',
                          borderRadius: 4,
                          background: redGradient,
                          color: whiteColor,
                          position: 'relative',
                          overflow: 'hidden',
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          boxShadow: `0 4px 30px ${secondaryColor}30, 0 0 60px ${secondaryColor}10`,
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: '-100%',
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)',
                            transition: 'all 0.6s ease',
                          },
                          '&:hover': {
                            transform: 'translateY(-3px) scale(1.02)',
                            boxShadow: `0 8px 40px ${secondaryColor}40, 0 0 80px ${secondaryColor}20`,
                            '&::before': {
                              left: '100%',
                            },
                          },
                          '&:active': {
                            transform: 'translateY(-1px) scale(0.98)',
                          },
                          '&.Mui-disabled': {
                            background: 'rgba(198, 40, 40, 0.4)',
                            color: 'rgba(255,255,255,0.3)',
                          },
                        }}
                        endIcon={!isLoading && <KeyboardArrowRightIcon />}
                      >
                        {isLoading ? 'Signing in...' : 'Sign In'}
                      </Button>

                      <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'rgba(255,255,255,0.4)',
                            fontSize: '0.9rem',
                          }}
                        >
                          Don't have an account?{' '}
                          <Button
                            component={Link}
                            to="/signup"
                            sx={{
                              color: primaryLight,
                              textTransform: 'none',
                              fontWeight: 600,
                              '&:hover': {
                                background: 'transparent',
                                textDecoration: 'underline',
                              },
                            }}
                            startIcon={<PersonAddOutlinedIcon />}
                          >
                            Sign Up
                          </Button>
                        </Typography>
                      </Box>

                      <Divider
                        sx={{
                          my: 3,
                          width: '100%',
                          borderColor: 'rgba(255,255,255,0.05)',
                          '&::before, &::after': {
                            borderColor: 'rgba(255,255,255,0.05)',
                          },
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'rgba(255,255,255,0.2)',
                            px: 2,
                            letterSpacing: 2,
                          }}
                        >
                          CHEMICAL DATABASE
                        </Typography>
                      </Divider>

                    <Box sx={{ textAlign: 'center', mt: 1 }}>
  {/* e-Neurons Technologies Ltd Link */}
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0.5,
    }}
  >
  <Typography
  variant="caption"
  sx={{
    color: '#FFFFFF',
    fontSize: '0.65rem',
    letterSpacing: 0.3,
  }}
>
  Powered by{' '}
  <a 
    href="https://e-neurons.com.ng" 
    target="_blank" 
    rel="noopener noreferrer"
    style={{ 
      color: '#FFFFFF', 
      textDecoration: 'none',
      fontWeight: 'bold'
    }}
  >
    e-Neurons Technologies Ltd
  </a>
</Typography>
  
    
     
      
    
  </Box>

  <Typography
    variant="body2"
    sx={{
      color: 'rgba(255,255,255,0.12)',
      fontSize: '0.5rem',
      mt: 0.5,
      letterSpacing: 0.5,
    }}
  >
    &copy; {new Date().getFullYear()} Chemical Database. All rights reserved.
  </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            color: 'rgba(255,255,255,0.15)',
                            fontSize: '0.65rem',
                            mt: 0.5,
                          }}
                        >
                          &copy; {new Date().getFullYear()} Chemical Database. All rights reserved.
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Validation Error Dialog */}
      <ValidationErrorDialog
        open={showValidationDialog}
        onClose={() => setShowValidationDialog(false)}
        errors={validationErrors}
        userType={validationUserType}
      />
    </>
  );
};

export default LoginPage; 